/**
 * Version History Component
 * Displays and manages version snapshots of reports
 */

import React, { useState, useEffect } from 'react';
import axios from '@/lib/http';
import { History, Save, Eye, Clock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Version {
  id: string;
  report_id: string;
  version_number: number;
  created_by: string;
  created_by_type: 'user' | 'accountant';
  created_by_name: string;
  created_at: string;
  change_summary: string;
}

interface VersionHistoryProps {
  reportId: string;
  getAuthHeader: () => any;
}

export default function VersionHistory({ reportId, getAuthHeader }: VersionHistoryProps) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, [reportId]);

  const fetchVersions = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/versions`,
        { headers: getAuthHeader() }
      );
      setVersions(response.data);
    } catch (err: any) {
      console.error('Failed to fetch versions:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changeSummary.trim()) return;

    setCreating(true);
    setError('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/versions`,
        {
          report_id: reportId,
          change_summary: changeSummary.trim()
        },
        { headers: getAuthHeader() }
      );

      setVersions([response.data, ...versions]);
      setChangeSummary('');
      setShowCreateModal(false);
    } catch (err: any) {
      console.error('Failed to create version:', err);
      setError(err.response?.data?.detail || 'Failed to create version');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="border rounded-lg p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5" style={{ color: 'var(--color-needs-human-text)' }} />
          <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Version History</h3>
          {versions.length > 0 && (
            <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
              {versions.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
          style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
        >
          <Save className="h-4 w-4" />
          Create Snapshot
        </button>
      </div>

      {error && (
        <div className="border px-4 py-2 rounded mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
          {error}
        </div>
      )}

      {/* Version List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Loading version history...</div>
        ) : versions.length === 0 ? (
          <div className="text-sm italic text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
            No versions yet. Create a snapshot to track changes.
          </div>
        ) : (
          versions.map((version, index) => (
            <div
              key={version.id}
              className="border rounded-lg p-4"
              style={
                index === 0
                  ? { borderColor: 'var(--color-verified)', backgroundColor: 'transparent' }
                  : { borderColor: 'var(--color-border)' }
              }
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Version {version.version_number}
                    </span>
                    {index === 0 && (
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}>
                        Current
                      </span>
                    )}
                  </div>

                  <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>{version.change_summary}</p>

                  <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{version.created_by_name}</span>
                      <span className="ml-1 px-2 py-0.5 rounded" style={
                        version.created_by_type === 'accountant'
                          ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                          : { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                      }>
                        {version.created_by_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(version.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Version Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Create Version Snapshot
            </h3>

            <form onSubmit={handleCreateVersion}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  What changed in this version?
                </label>
                <textarea
                  value={changeSummary}
                  onChange={(e) => setChangeSummary(e.target.value)}
                  placeholder="e.g., Updated calculation methodology based on new evidence"
                  className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                  rows={4}
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setChangeSummary('');
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !changeSummary.trim()}
                  className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {creating ? 'Creating...' : 'Create Snapshot'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
